<template>
    <div class="modal-mask" @click="emit('hide')" @keydown.esc="emit('hide')">
        <div class="modal-wrapper">
            <div class="modal-container" @click.stop="" :style="{maxWidth: width ? width + 'px'  : undefined}">
                <span class="close" @click="emit('hide')" v-if="closeButton" id="conf2-modal-close"></span>
                <div class="modal-body">
                    <slot></slot>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {onMounted, onBeforeUnmount} from 'vue';
import {isTablet} from "@/plugins/Helpers";

interface Props {
    name?: string;
    width?: number;
    closeButton?: boolean;
}

const {name, width, closeButton = true} = defineProps<Props>();

const emit = defineEmits<{
    hide: [];
}>();

let modalElement: HTMLElement | null = null;

const keyEsc = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
        emit('hide');
    }
};

const onInputFocus = () => {
    window.visualViewport?.addEventListener('resize', onWindowResizeAfterInputFocus);
    setTimeout(() => {
        window.visualViewport?.removeEventListener('resize', onWindowResizeAfterInputFocus);
    }, 1500);
};

const onWindowResizeAfterInputFocus = () => {
    window.scrollTo(0, 0);
    window.visualViewport?.removeEventListener('resize', onWindowResizeAfterInputFocus);
};

onMounted(() => {
    modalElement = document.querySelector('.modal-mask');
    document.addEventListener('keydown', keyEsc);
    if (isTablet() && modalElement) {
        for (let input of modalElement.querySelectorAll('input[type=text], textarea')) {
            input.addEventListener('focus', onInputFocus);
        }
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('keydown', keyEsc);
    if (modalElement) {
        for (let input of modalElement.querySelectorAll('input[type=text], textarea')) {
            input.removeEventListener('focus', onInputFocus);
        }
    }
});
</script>

<style scoped lang="scss">
.modal-mask {
    -webkit-touch-callout: default;
    -webkit-user-select: text;
    -khtml-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
    user-select: text;
}

:deep(.modal-body) {
    font-size: 15px;
    line-height: 24px;
}

.modal-mask {
    position: fixed;
    z-index: 13000;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(var(--vh, 1vh) * 100);
    background-color: rgba(0, 0, 0, .3);
    display: table;
    transition: opacity .3s ease;

    .is-responsive & {
        display: block;
    }
}

.modal-wrapper {
    display: table-cell;
    vertical-align: middle;
    padding: 20px;

    .is-responsive & {
        display: block;
        margin: 82px 20px 0 20px;
        padding: 0;

        @media(max-width: 480px) {
            margin-left: 10px;
            margin-right: 10px;
        }

        @media(max-width: 360px) {
            margin: 68px 0 0;
        }
    }
}

.modal-container {
    max-width: 1100px;
    width: 100%;
    margin: 0px auto;
    background-color: #fff;
    transition: all .3s ease;
    position: relative;
}

.close {
    position: absolute;
    top: 38px;
    right: 38px;
    cursor: pointer;
    height: 20px;
    width: 20px;
    display: block;
    z-index: 2;

    .is-responsive div & {
        top: 20px;
        right: 20px;
    }

    &:before {
        content: '';
        display: block;
        width: 21px;
        height: 1px;
        background: #111111;
        -webkit-transform: rotate(45deg);
        -moz-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        -o-transform: rotate(45deg);
        transform: rotate(45deg);
        position: relative;
        top: 1px;
        left: 0;
        margin-top: 8px;
    }

    &:after {
        content: '';
        display: block;
        width: 21px;
        height: 1px;
        background: #111111;
        -webkit-transform: rotate(-45deg);
        -moz-transform: rotate(-45deg);
        -ms-transform: rotate(-45deg);
        -o-transform: rotate(-45deg);
        transform: rotate(-45deg);
        position: relative;
        top: 0;
        left: 0;
    }
}

.modal-body {
    .is-responsive & {
        overflow-y: auto;
        max-height: calc(100 * var(--vh, 1vh) - 95px);

        @media(max-width: 360px) {
            max-height: calc(100 * var(--vh, 1vh) - 61px)
        }
    }
}

.modal-default-button {
    float: right;
}

/*
 * The following styles are auto-applied to elements with
 * transition="modal" when their visibility is toggled
 * by Vue.js.
 *
 * You can easily play with the modal transition by editing
 * these styles.
 */

.modal-enter {
    opacity: 0;
}

.modal-leave-active {
    opacity: 0;
}

.modal-enter .modal-container,
.modal-leave-active .modal-container {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
}

.is-responsive div {
    :deep(.conf2-save-right) {
        padding: 40px 20px 20px;
    }
}


:deep(.conf2-save-right) {
    padding: 80px;

    a {
        text-decoration: underline;
        font-weight: 500;

        &:hover {
            text-decoration: none;
            color: #000;
        }
    }

    .agb {
        max-width: 480px;
        margin-bottom: 24px;

        .form-check-input {
            float: left;
            margin-right: 8px;
            width: 16px;
            height: 16px;
            border-radius: 0;
            border: 1px solid #CACBCC;
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;

            &:checked {
                background-color: #fff;
                border-color: #000;
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3e%3c/svg%3e");
            }
        }

        label {
            font-size: 12px;
            line-height: 13px;
            margin-bottom: 20px;
            padding-top: 2px;

            a {
                text-decoration: underline;
                font-weight: 500;

                &:hover {
                    text-decoration: none;
                    color: #000;
                }
            }
        }
    }

    .form-group {
        max-width: 480px;
        margin-bottom: 24px;

        label {
            font-size: 12px;
            line-height: 13px;
            margin-bottom: 4px;
        }

        .form-control {
            border: 1px solid #CACBCC;
            font-size: 15px;
            line-height: 20px;
            padding: 14px 17px;
            border-radius: 2px;

            &:not(textarea) {
                height: 48px;
            }
        }
    }

    h4 {
        font-size: 24px;
        font-weight: bold;
        letter-spacing: 0;
        line-height: 32px;
        margin-bottom: 28px;
    }

    p {
        font-size: 15px !important;
        line-height: 24px !important;;
        margin-bottom: 28px;
        opacity: 0.8;

        .is-responsive div & {
            font-size: 14px !important;
            line-height: 20px !important;
        }
    }

    ul {
        margin: 0;
        padding: 0;

        li {
            position: relative;
            padding-left: 20px;
            font-size: 15px;
            line-height: 24px;
            letter-spacing: 0;
            margin-bottom: 8px;
            list-style: none;

            .is-responsive div & {
                font-size: 14px;
                line-height: 20px;
            }

            &:before {
                position: absolute;
                left: 2px;
                top: 8px;
                content: "";
                width: 8px;
                height: 8px;
                background: #FFD24D;
            }
        }
    }
}

:deep(.btn) {
    .is-responsive div & {
        display: block;
    }

    cursor: pointer;
    text-align: center;
}

:deep(.conf-help-wrap) {
        .h1, .h2, .h3, .h4, .h5, .h6, h1, h2, h3, h4, h5, h6 {
            color: #000;
            font-size: 40px;
            font-weight: 700;
            line-height: 1.2;
        }


        .h1, h1 {
            font-size: 80px
        }

        .h2, h2 {
            font-size: 60px
        }

        .h3, h3 {
            font-size: 40px
        }

        .h4, h4 {
            font-size: 32px
        }

        .h5, h5 {
            font-size: 24px
        }
    }

</style>
